import { Text } from "@softmax/design-system";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

const styles = stylex.create({
  root: { display: "grid", gap: spacing.space8, minWidth: 0 },
  strip: { display: "flex", flexWrap: "wrap", gap: spacing.space8 },
  column: { display: "grid", gap: spacing.space8, minWidth: 0 },
  leader: {
    boxSizing: "border-box",
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: radii.small,
    borderStyle: "solid",
    borderWidth: 1,
    display: "grid",
    fontFamily: fonts.sans,
    fontSize: fontSizes.small,
    gap: spacing.space4,
    minWidth: 0,
    padding: spacing.space8,
    outline: {
      default: "none",
      ":focus-visible": `2px solid ${colors.foreground}`,
    },
    outlineOffset: 2,
  },
  compact: { width: "100%" },
  category: { color: colors.subtle, fontWeight: 500, overflowWrap: "anywhere" },
  line: {
    alignItems: "baseline",
    display: "flex",
    gap: spacing.space8,
    justifyContent: "space-between",
    minWidth: 0,
  },
  names: { minWidth: 0 },
  owner: {
    color: colors.foreground,
    display: "block",
    fontWeight: 500,
    overflowWrap: "anywhere",
  },
  holder: {
    color: colors.muted,
    display: "block",
    overflowWrap: "anywhere",
  },
  value: {
    flexShrink: 0,
    color: colors.foreground,
    fontVariantNumeric: "tabular-nums",
    maxWidth: "60%",
    textAlign: "end",
  },
  unit: {
    color: colors.muted,
    fontSize: fontSizes.caption,
    marginInlineStart: spacing.space4,
  },
});
export type LeagueSuperlative = {
  readonly key: string;
  readonly category: string;
  readonly holder: string;
  readonly owner: string | null;
  readonly value: string;
  readonly unit: string;
};
export function LeagueSuperlatives({
  superlatives,
  compact = false,
}: {
  readonly superlatives: readonly LeagueSuperlative[];
  readonly compact?: boolean;
}) {
  if (superlatives.length === 0) return null;

  return (
    <section aria-label="League leaders" {...stylex.props(styles.root)}>
      <Text size="small" tone="muted">
        League leaders
      </Text>
      <div {...stylex.props(compact ? styles.column : styles.strip)}>
        {superlatives.map((item) => (
          <div
            key={item.key}
            {...stylex.props(styles.leader, compact && styles.compact)}
          >
            <span {...stylex.props(styles.category)}>{item.category}</span>
            <div {...stylex.props(styles.line)}>
              <span {...stylex.props(styles.names)}>
                <span {...stylex.props(styles.owner)}>
                  {item.owner ?? item.holder}
                </span>
                {item.owner && (
                  <span {...stylex.props(styles.holder)}>{item.holder}</span>
                )}
              </span>
              <span {...stylex.props(styles.value)}>
                {item.value}
                {item.unit && (
                  <span {...stylex.props(styles.unit)}>{item.unit}</span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
